// Block Clash / Tetris Battle Server Engine (2-4 Players)
export class TetrisEngine {
  constructor(io, room) {
    this.io = io;
    this.room = room;
    this.playerBoards = {}; // playerId -> 20x10 matrix
    this.playerScores = {};
    this.alivePlayers = new Set();
    this.botInterval = null;
  }

  start() {
    this.playerBoards = {};
    this.playerScores = {};
    this.alivePlayers = new Set();

    // Preserve bot flag and auto-fill bot if solo
    this.room.players = this.room.players.map(p => ({
      ...p,
      isBot: !!p.isBot || p.id.startsWith('bot_')
    }));

    if (this.room.players.length < 2) {
      this.room.players.push({
        id: 'bot_tetris',
        name: '🤖 BlockBot',
        avatar: '🤖',
        isBot: true
      });
    }

    this.room.players.forEach((p) => {
      this.playerBoards[p.id] = Array(20).fill(null).map(() => Array(10).fill(0));
      this.playerScores[p.id] = 0;
      this.alivePlayers.add(p.id);
    });

    this.io.to(this.room.code).emit('tetris_started', {
      players: this.room.players.map((p) => ({ id: p.id, name: p.name, avatar: p.avatar }))
    });

    // Run active bot simulation for any AI bots in match
    const bots = this.room.players.filter(p => p.isBot);
    if (bots.length > 0) {
      let botTick = 0;
      if (this.botInterval) clearInterval(this.botInterval);
      this.botInterval = setInterval(() => {
        botTick++;
        bots.forEach(bot => {
          if (!this.alivePlayers.has(bot.id)) return;
          const board = this.playerBoards[bot.id];
          if (!board) return;

          // Simulate block stacking on bottom rows
          const targetRow = 16 + (botTick % 4);
          const col = (botTick * 3) % 10;
          if (board[targetRow]) {
            board[targetRow][col] = 1;
            board[targetRow][(col + 1) % 10] = 1;
          }

          // Every 5-6 ticks (~8s), bot clears 2 rows and sends garbage line!
          if (botTick % 5 === 0) {
            // Clear row
            board[19] = Array(10).fill(0);
            this.io.to(this.room.code).emit('tetris_garbageSent', {
              fromId: bot.id,
              count: 1
            });
          }

          this.io.to(this.room.code).emit('tetris_syncBoards', {
            boards: this.playerBoards
          });
        });
      }, 1600);
    }
  }

  handleBoardUpdate(playerId, board, linesCleared) {
    this.playerBoards[playerId] = board;

    if (linesCleared >= 2) {
      // Send garbage lines to other players!
      const garbageCount = linesCleared === 4 ? 4 : linesCleared - 1;
      this.io.to(this.room.code).emit('tetris_garbageSent', {
        fromId: playerId,
        count: garbageCount
      });
    }

    this.io.to(this.room.code).emit('tetris_syncBoards', {
      boards: this.playerBoards
    });
  }

  handlePlayerDeath(playerId) {
    this.alivePlayers.delete(playerId);
    this.io.to(this.room.code).emit('tetris_playerDead', { playerId });

    if (this.alivePlayers.size <= 1 && this.room.players.length > 1) {
      if (this.botInterval) clearInterval(this.botInterval);
      const winnerId = Array.from(this.alivePlayers)[0];
      const winner = this.room.players.find((p) => p.id === winnerId);
      this.io.to(this.room.code).emit('tetris_gameOver', { winner });
    }
  }

  destroy() {
    if (this.botInterval) clearInterval(this.botInterval);
  }
}
